import { Modal } from '@components/core/modal.component';
import { Button } from '@components/ui/button';
import { type FunctionComponent, type ReactNode, useState } from 'react';

type ConfirmationModalVariant = 'default' | 'destructive';

type ConfirmationModalProps = {
  cancelLabel?: string;
  confirmLabel?: string;
  description: ReactNode;
  isOpen: boolean;
  isPending?: boolean;
  onConfirm: () => Promise<void> | void;
  setIsOpen: (isOpen: boolean) => void;
  title?: string;
  variant?: ConfirmationModalVariant;
  width?: string;
};

type ConfirmDeleteModalProps = {
  isOpen: boolean;
  itemName: string;
  onConfirm: () => Promise<void>;
  setIsOpen: (isOpen: boolean) => void;
  /**
   * Whether to show the "This action cannot be undone" warning.
   * @default true
   */
  showUndoWarning?: boolean;
};

export const ConfirmationModal: FunctionComponent<ConfirmationModalProps> = (props) => {
  const {
    cancelLabel = 'Cancel',
    confirmLabel = 'Confirm',
    description,
    isOpen,
    isPending = false,
    onConfirm,
    setIsOpen,
    title = 'Are you sure?',
    variant = 'default',
    width = '500px',
  } = props;
  const [isConfirming, setIsConfirming] = useState(false);
  const isBusy = isConfirming || isPending;

  const handleConfirm = async (): Promise<void> => {
    setIsConfirming(true);
    try {
      await onConfirm();
      setIsOpen(false);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Modal
      centered
      isOpen={isOpen}
      onClose={() => {
        if (!isBusy) setIsOpen(false);
      }}
      width={width}
    >
      <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-2">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          <div className="text-gray-600">{description}</div>
        </div>
        <div className="flex justify-end gap-3">
          <Button
            disabled={isBusy}
            onClick={() => setIsOpen(false)}
            variant="secondary"
          >
            {cancelLabel}
          </Button>
          <Button
            disabled={isBusy}
            onClick={() => void handleConfirm()}
            variant={variant === 'destructive' ? 'destructive' : 'primary'}
          >
            {isBusy ? 'Working...' : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const ConfirmDeleteModal: FunctionComponent<ConfirmDeleteModalProps> = (props) => {
  const { isOpen, itemName, onConfirm, setIsOpen, showUndoWarning = true } = props;

  return (
    <ConfirmationModal
      confirmLabel="Remove"
      description={
        <>
          <p>Are you sure you want to remove the {itemName}?</p>
          {showUndoWarning && <p className="mt-2">This action cannot be undone.</p>}
        </>
      }
      isOpen={isOpen}
      onConfirm={onConfirm}
      setIsOpen={setIsOpen}
      variant="destructive"
    />
  );
};
